/**
 * User and role administration. Super Admin only.
 *
 * Users are deactivated rather than deleted, so audit entries, authorship and revision history stay
 * attributable. Password resets produce a one-time password shown once, which the user must change
 * at next sign-in.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, Checkbox, FormField, Input, Modal, Spinner } from '@/components/ui';
import { DataTable, type Column } from '@/components/admin/DataTable';
import { LoadError } from '@/components/site/blocks';
import { authApi, cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';
import type { RoleKey, UserRecord } from '@/services/types';

function UserDialog({ user, onClose }: { user: UserRecord | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const rolesQuery = useQuery({ queryKey: ['auth', 'roles'], queryFn: authApi.roles });

  const [form, setForm] = useState({
    email: user?.email ?? '',
    full_name: user?.full_name ?? '',
    job_title: user?.job_title ?? '',
    is_active: user?.is_active ?? true,
    roles: (user?.roles ?? ['viewer']) as RoleKey[],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const mutation = useMutation({
    mutationFn: () => (user ? cmsApi.updateUser(user.id, form) : cmsApi.createUser(form)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'users'] });
      onClose();
    },
    onError: (caught) => {
      setErrors(caught instanceof ApiError ? caught.fields : { __all__: 'Saving failed.' });
    },
  });

  function toggleRole(role: RoleKey) {
    setForm((current) => ({
      ...current,
      roles: current.roles.includes(role) ? current.roles.filter((item) => item !== role) : [...current.roles, role],
    }));
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={user ? `Edit ${user.full_name}` : 'Invite a CMS user'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            {user ? 'Save changes' : 'Create user'}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {errors.__all__ && <Alert tone="danger">{errors.__all__}</Alert>}

        <div className="grid gap-5 sm:grid-cols-2">
          <FormField label="Email" htmlFor="user-email" required {...(errors.email ? { error: errors.email } : {})}>
            <Input
              id="user-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm({ ...form, email: event.target.value })}
              error={Boolean(errors.email)}
            />
          </FormField>
          <FormField label="Full name" htmlFor="user-name" required {...(errors.full_name ? { error: errors.full_name } : {})}>
            <Input
              id="user-name"
              value={form.full_name}
              onChange={(event) => setForm({ ...form, full_name: event.target.value })}
              error={Boolean(errors.full_name)}
            />
          </FormField>
          <FormField label="Job title" htmlFor="user-title">
            <Input id="user-title" value={form.job_title} onChange={(event) => setForm({ ...form, job_title: event.target.value })} />
          </FormField>
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-ink">Roles</legend>
          <p className="mt-1 text-xs text-muted">
            A user can hold more than one role; permissions are the union of them.
          </p>
          {rolesQuery.isLoading ? (
            <Spinner />
          ) : (
            <div className="mt-3 space-y-3">
              {(rolesQuery.data ?? []).map((role) => (
                <div key={role.key} className="rounded-md border border-line p-3">
                  <Checkbox
                    id={`role-${role.key}`}
                    checked={form.roles.includes(role.key)}
                    onChange={() => toggleRole(role.key)}
                    label={
                      <>
                        <span className="font-medium text-ink">{role.name}</span>
                        <span className="mt-0.5 block text-xs text-muted">{role.description}</span>
                      </>
                    }
                  />
                </div>
              ))}
            </div>
          )}
          {errors.roles && (
            <p role="alert" className="mt-2 text-xs font-medium text-danger">
              {errors.roles}
            </p>
          )}
        </fieldset>

        <Checkbox
          id="user-active"
          checked={form.is_active}
          onChange={(event) => setForm({ ...form, is_active: event.target.checked })}
          label="Account is active (can sign in)"
        />

        {!user && (
          <Alert tone="info">
            A strong temporary password is generated. The user must change it at first sign-in — use the reset action to
            retrieve and share it.
          </Alert>
        )}
      </div>
    </Modal>
  );
}

export function UsersPage() {
  const queryClient = useQueryClient();
  const [dialogUser, setDialogUser] = useState<UserRecord | null | undefined>(undefined);
  const [temporaryPassword, setTemporaryPassword] = useState<{ email: string; password: string } | null>(null);
  const [page, setPage] = useState(1);

  const listQuery = useQuery({ queryKey: ['cms', 'users', page], queryFn: () => cmsApi.users({ page, page_size: 20 }) });
  const rolesQuery = useQuery({ queryKey: ['auth', 'roles'], queryFn: authApi.roles });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => cmsApi.deactivateUser(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cms', 'users'] }),
  });

  const activateMutation = useMutation({
    mutationFn: (id: number) => cmsApi.activateUser(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['cms', 'users'] }),
  });

  const resetMutation = useMutation({
    mutationFn: (user: UserRecord) => cmsApi.resetUserPassword(user.id).then((result) => ({ result, user })),
    onSuccess: ({ result, user }) => setTemporaryPassword({ email: user.email, password: result.temporary_password }),
  });

  const columns: Column<UserRecord>[] = [
    {
      key: 'full_name',
      header: 'User',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-3">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-soft text-xs font-bold text-primary">
            {row.initials}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-ink">{row.full_name}</p>
            <p className="truncate text-xs text-muted">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'roles',
      header: 'Roles',
      render: (row) => (
        <ul className="flex flex-wrap gap-1.5">
          {row.roles.map((role) => (
            <li key={role}>
              <Badge tone={role === 'super_admin' ? 'signal' : 'primary'}>{role.replace('_', ' ')}</Badge>
            </li>
          ))}
        </ul>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      width: '7rem',
      render: (row) => <Badge tone={row.is_active ? 'success' : 'neutral'}>{row.is_active ? 'Active' : 'Inactive'}</Badge>,
    },
    {
      key: 'last_login',
      header: 'Last sign-in',
      sortable: true,
      width: '10rem',
      render: (row) => (
        <span className="text-muted">
          {row.last_login ? new Date(row.last_login).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) : 'Never'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '14rem',
      render: (row) => (
        <div className="flex justify-end gap-3 text-sm">
          <button type="button" onClick={() => setDialogUser(row)} className="font-medium text-primary underline">
            Edit
          </button>
          <button type="button" onClick={() => resetMutation.mutate(row)} className="font-medium text-muted underline">
            Reset password
          </button>
          {row.is_active ? (
            <button
              type="button"
              onClick={() => {
                if (window.confirm(`Deactivate ${row.full_name}? They will no longer be able to sign in.`)) {
                  deactivateMutation.mutate(row.id);
                }
              }}
              className="font-medium text-danger underline"
            >
              Deactivate
            </button>
          ) : (
            <button type="button" onClick={() => activateMutation.mutate(row.id)} className="font-medium text-success underline">
              Reactivate
            </button>
          )}
        </div>
      ),
    },
  ];

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Users &amp; roles</h1>
          <p className="mt-1 text-sm text-muted">Who can sign in to the CMS, and what each of them may do.</p>
        </div>
        <Button onClick={() => setDialogUser(null)}>Invite a user</Button>
      </div>

      {temporaryPassword && (
        <Alert tone="warning" title="Temporary password — shown only once">
          <p>
            Share this with <strong>{temporaryPassword.email}</strong> over a secure channel. They must change it at
            first sign-in.
          </p>
          <p className="mt-2 inline-block rounded bg-surface px-3 py-2 font-mono text-sm">{temporaryPassword.password}</p>
          <button type="button" onClick={() => setTemporaryPassword(null)} className="mt-2 block text-xs font-semibold underline">
            I have copied it
          </button>
        </Alert>
      )}

      <DataTable
        caption="CMS users"
        columns={columns}
        rows={listQuery.data?.results ?? []}
        count={listQuery.data?.count ?? 0}
        page={listQuery.data?.page ?? 1}
        pages={listQuery.data?.pages ?? 1}
        isLoading={listQuery.isLoading}
        onPage={setPage}
      />

      <Card>
        <h2 className="text-base font-semibold text-ink">What each role can do</h2>
        <ul className="mt-4 divide-y divide-line">
          {(rolesQuery.data ?? []).map((role) => (
            <li key={role.key} className="flex flex-wrap items-start justify-between gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">{role.name}</p>
                <p className="mt-0.5 text-sm text-muted">{role.description}</p>
              </div>
              <Badge tone="neutral">
                {role.user_count} user{role.user_count === 1 ? '' : 's'}
              </Badge>
            </li>
          ))}
        </ul>
      </Card>

      {dialogUser !== undefined && <UserDialog user={dialogUser} onClose={() => setDialogUser(undefined)} />}
    </div>
  );
}
